import React, { useState, useEffect } from 'react';

interface InsuranceData {
  numberPlate: string;
  insurerCompanyName: string;
  policyNumber: string;
  policyHolderName: string;
  policyHolderAddress: string;
  coverageStartDate: string;
  coverageEndDate: string;
  coverageType: string;
  issuedDate: string;
}

interface FormErrors {
  [key: string]: string;
}

interface Config {
}

interface IsLoading {
  config: boolean;
}

interface StepFourProps {
  insuranceData: InsuranceData;
  setInsuranceData: React.Dispatch<React.SetStateAction<InsuranceData>>;
  insuranceImages: File[];
  setInsuranceImages: React.Dispatch<React.SetStateAction<File[]>>;
  formErrors: FormErrors;
  config: Config;
  isLoading: IsLoading;
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    maxSizeMB: number,
    allowedTypes: string[]
  ) => void;
}
const todayStr=new Date().toISOString().split('T')[0];
const clampToToday =(v:string)=>(v && v>todayStr?todayStr:v||'');
const StepFour: React.FC<StepFourProps> = ({
  insuranceData,
  setInsuranceData,
  insuranceImages,
  setInsuranceImages,
  formErrors,
  isLoading,
  handleFileChange,
}) => {
  const [insuranceImagePreviews, setInsuranceImagePreviews] = useState<string[]>([]);
  const maxFileSizeMB = 5;
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

  // Generate image previews
  const generatePreviews = (files: File[], setPreviews: React.Dispatch<React.SetStateAction<string[]>>) => {
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => {
      prev.forEach(URL.revokeObjectURL);
      return previews;
    });
  };
  // Put this under your other consts near todayStr/clampToToday:
const coverageTypes = [
  'Third-Party Liability (TPL)',
  'Comprehensive',
  'Own Damage (OD) Only',
  'Third-Party, Fire & Theft (TPFT)',
  'PA – Owner/Driver',
  'PA – Paid Driver',
  'PA – Passengers',
  'Zero Depreciation',
  'Engine & Gearbox Protect',
  'Return to Invoice (RTI)',
  'NCB Protection',
  'Roadside Assistance',
  'Consumables Cover',
  'Key/Lock Replacement',
  'Windscreen Cover',
  'Accessories Cover',
  'Personal Belongings Cover',
  'Tire/Rim Protection',
  'Natural Perils',
  'Riot & Strike (SRCC)',
  'Terrorism Cover',
  'Rideshare Endorsement',
  'Rental Reimbursement',
  'Gap Insurance',
];

// Inside StepFour component (with other useState hooks)
const [covOpen, setCovOpen] = useState(false);
const [covQuery, setCovQuery] = useState(insuranceData.coverageType || '');
const boxRef = React.useRef<HTMLDivElement>(null);

// Keep input text in sync when the selected coverage type changes outside
useEffect(() => {
  setCovQuery(insuranceData.coverageType || '');
}, [insuranceData.coverageType]);

// Close dropdown on outside click
useEffect(() => {
  const onClick = (e: MouseEvent) => {
    if (boxRef.current && !boxRef.current.contains(e.target as Node)) setCovOpen(false);
  };
  document.addEventListener('mousedown', onClick);
  return () => document.removeEventListener('mousedown', onClick);
}, []);

const filteredCoverage = coverageTypes.filter(t =>
  t.toLowerCase().includes((covQuery || '').toLowerCase())
);

const selectCoverage = (val: string) => {
  setInsuranceData({ ...insuranceData, coverageType: val });
  setCovQuery(val);
  setCovOpen(false);
};

  // Handle file removal
  const removeFile = (
    index: number,
    files: File[],
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    previews: string[],
    setPreviews: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    updatedPreviews.forEach(URL.revokeObjectURL);
  };

  // Update previews when files change
  useEffect(() => {
    generatePreviews(insuranceImages, setInsuranceImagePreviews);
    return () => insuranceImagePreviews.forEach(URL.revokeObjectURL);
  }, [insuranceImages]);

  // Clear images if numberPlate changes
  useEffect(() => {
    if (!insuranceData.numberPlate) {
      setInsuranceImages([]);
      setInsuranceImagePreviews([]);
    }
  }, [insuranceData.numberPlate, setInsuranceImages]);

  return (
    <div className="container">
      <div className="mb-4">
        <h5>Insurance Records</h5>
        <div className="row g-4">
          {/* Left Column */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label mb-2">Number Plate</label>
              <input
                type="text"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={insuranceData.numberPlate || ''}
                disabled
                placeholder="Number plate set in previous step"
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Insurer Company Name</label>
              <input
                type="text"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={insuranceData.insurerCompanyName || ''}
                onChange={(e) => setInsuranceData({ ...insuranceData, insurerCompanyName: e.target.value })}
                placeholder="Enter insurer company name"
                disabled={isLoading.config}
              />
              {formErrors.insurerCompanyName && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.insurerCompanyName}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Policy Number</label>
              <input
                type="text"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={insuranceData.policyNumber || ''}
                onChange={(e) => setInsuranceData({ ...insuranceData, policyNumber: e.target.value })}
                placeholder="Enter policy number"
                disabled={isLoading.config}
              />
              {formErrors.policyNumber && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.policyNumber}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Policy Holder Name</label>
              <input
                type="text"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={insuranceData.policyHolderName || ''}
                onChange={(e) => setInsuranceData({ ...insuranceData, policyHolderName: e.target.value })}
                placeholder="Enter policy holder name"
                disabled={isLoading.config}
              />
              {formErrors.policyHolderName && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.policyHolderName}
                </div>
              )}
            </div>
                        <div className="mb-3">
              <label className="col-form-label mb-2">Insurance Images</label>
              <input
                type="file"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                multiple
                accept={allowedTypes.join(',')}
                onChange={(e) => handleFileChange(e, setInsuranceImages, maxFileSizeMB, allowedTypes)}
                disabled={isLoading.config}
              />
              <small className="text-muted">Max {maxFileSizeMB}MB per file, formats: JPEG, PNG, JPG</small>
              {formErrors.insuranceImages && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.insuranceImages}
                </div>
              )}
              {insuranceImages.length > 0 && (
                <div className="mt-3">
                  <h6>Selected Files:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {insuranceImages.map((file, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={insuranceImagePreviews[index]}
                          alt={file.name}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0"
                          style={{ padding: '2px 6px' }}
                          onClick={() =>
                            removeFile(index, insuranceImages, setInsuranceImages, insuranceImagePreviews, setInsuranceImagePreviews)
                          }
                          disabled={isLoading.config}
                        >
                          
                        </button>
                        <div style={{ fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Right Column */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label mb-2">Policy Holder Address</label>
              <input
                type="text"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={insuranceData.policyHolderAddress || ''}
                onChange={(e) => setInsuranceData({ ...insuranceData, policyHolderAddress: e.target.value })}
                placeholder="Enter policy holder address"
                disabled={isLoading.config}
              />
              {formErrors.policyHolderAddress && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.policyHolderAddress}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Issued Date</label>
              <input
                type="date"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={insuranceData.issuedDate || ''}
                max={todayStr}
                onChange={(e) => setInsuranceData({ ...insuranceData, issuedDate:clampToToday (e.target.value) })}
                disabled={isLoading.config}
              />
              {formErrors.issuedDate && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.issuedDate}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Coverage Start Date</label>
              <input
                type="date"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={insuranceData.coverageStartDate || ''}
                max={todayStr}
                onChange={(e) => setInsuranceData({ ...insuranceData, coverageStartDate: clampToToday(e.target.value) })}
                disabled={isLoading.config}
              />
              {formErrors.coverageStartDate && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.coverageStartDate}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Coverage End Date</label>
              <input
                type="date"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={insuranceData.coverageEndDate || ''}
                onChange={(e) => setInsuranceData({ ...insuranceData, coverageEndDate: e.target.value })}
                disabled={isLoading.config}
              />
              {formErrors.coverageEndDate && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.coverageEndDate}
                </div>
              )}
            </div>
            <div className="mb-3" ref={boxRef}>
              <label className="col-form-label mb-2">Coverage Type</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className="form-control"
                  style={{ width: '100%', padding: '10px 15px' }}
                  value={covQuery}
                  onFocus={() => !isLoading.config && setCovOpen(true)}
                  onChange={(e) => {
                    setCovQuery(e.target.value);
                    if (!covOpen) setCovOpen(true);
                  }}
                  placeholder="Search & select coverage type"
                  disabled={isLoading.config}
                  autoComplete="off"
                />
                {/* Clear button */}
                {covQuery && !isLoading.config && (
                  <button
                    type="button"
                    onClick={() => {
                      setCovQuery('');
                      setInsuranceData({ ...insuranceData, coverageType: '' });
                      setCovOpen(true);
                    }}
                    style={{
                      position: 'absolute', right: 8, top: 8, border: 'none',
                      background: 'transparent', fontSize: 18, lineHeight: 1, cursor: 'pointer'
                    }}
                    aria-label="Clear"
                  >
                    ×
                  </button>
                )}

                {/* Dropdown */}
                {covOpen && !isLoading.config && (
                  <div
                    style={{
                      position: 'absolute',
                      zIndex: 1000,
                      width: '100%',
                      maxHeight: 220,
                      overflowY: 'auto',
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      marginTop: 4,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}
                  >
                    {filteredCoverage.length === 0 ? (
                      <div style={{ padding: '10px 12px', color: '#777' }}>No matches</div>
                    ) : (
                      filteredCoverage.map((item) => (
                        <button
                          key={item}
                          type="button"
                          onClick={() => selectCoverage(item)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '10px 12px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onMouseDown={(e) => e.preventDefault()} // keep focus
                        >
                          {item}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              {formErrors.coverageType && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.coverageType}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default StepFour;